import { Button, FormControl, FormControlLabel, Radio, RadioGroup, Stack } from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { PageContainer } from "../../components/primitives/layout/PageContainer";
import { BackButton } from "../../components/domain/navigation/BackButton";
import { LoadingTitle } from "../../components/primitives/data-display/LoadingTitle";
import { Subtitle } from "../../components/primitives/data-display/Subtitle";
import { LoadingState } from "../../components/primitives/feedback/LoadingState";
import { EmptyState } from "../../components/primitives/feedback/EmptyState";
import { ErrorState } from "../../components/domain/feedback/ErrorState";
import { ClaimDisplay } from "../../components/domain/data-display/ClaimDisplay";
import { gaps } from "../../components/primitives/spacing";
import { Note } from "../../components/primitives/data-display/Note";
import { useContentReview } from "./useContentReview";
import { ROUTES } from "../../route_paths";
import { getClaimRefusal } from "../../types/claims";
import type { ClaimRefusalReason } from "../../types/claims";

const REASON_KEYS = {
  outside_health: "contentReview.reason.outside_health",
} as const satisfies Record<ClaimRefusalReason, string>;

/**
 * Extracts claims from the input text (or URL-fetched content) on mount.
 * If multiple claims are found, shows a radio picker for single selection.
 */
export default function ContentReview() {
  const {
    isLoading,
    apiError,
    rateLimitError,
    rawInput,
    claims,
    checkable,
    unchecked,
    selected,
    setSelected,
    handleVerify,
  } = useContentReview();
  const { t } = useTranslation();

  const minutes = rateLimitError ? Math.max(1, Math.ceil(rateLimitError.retryAfter / 60)) : 0;

  if (rateLimitError) {
    return (
      <PageContainer center>
        <EmptyState
          title={t("rateLimit.title")}
          message={t("rateLimit.message", { count: minutes })}
          action={
            <Button variant="outlined" component={Link} to={ROUTES.landing.path()}>
              {t("common.startOver")}
            </Button>
          }
        />
      </PageContainer>
    );
  }

  // Extraction-in-progress is a focused, centered screen (mirrors /analysing),
  // rather than a lone back button above a floating spinner.
  if (isLoading && claims === null && !apiError) {
    return (
      <PageContainer center>
        <LoadingTitle>{t("contentReview.extracting")}</LoadingTitle>
        {rawInput && <ClaimDisplay claim={rawInput} />}
        <LoadingState />
      </PageContainer>
    );
  }

  if (apiError) {
    return (
      <PageContainer center>
        <ErrorState
          code={apiError.code}
          action={
            <Button variant="outlined" component={Link} to={ROUTES.landing.path()}>
              {t("common.startOver")}
            </Button>
          }
        />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <BackButton to={ROUTES.landing.path()} />

      {claims !== null && claims.length === 0 && (
        <EmptyState
          title={t("contentReview.noClaimTitle")}
          message={t("contentReview.noClaimHint")}
          action={
            <Button variant="outlined" component={Link} to={ROUTES.landing.path()}>
              {t("common.startOver")}
            </Button>
          }
        />
      )}

      {claims !== null && claims.length > 0 && checkable.length === 0 && (
        <>
          <Subtitle>{t("contentReview.noneCheckable", { count: unchecked.length })}</Subtitle>
          {unchecked.map((claim) => (
            <Stack key={claim.id} gap={gaps.snug}>
              <ClaimDisplay claim={claim.source_text} />
              <Note>{t(REASON_KEYS[getClaimRefusal(claim.properties) ?? "outside_health"])}</Note>
            </Stack>
          ))}
          <Button variant="outlined" component={Link} to={ROUTES.landing.path()}>
            {t("common.startOver")}
          </Button>
        </>
      )}

      {checkable.length === 1 && (
        <>
          <Subtitle>{t("contentReview.foundOne")}</Subtitle>
          <ClaimDisplay claim={checkable[0].source_text} />
          <Button
            variant="contained"
            size="large"
            endIcon={<ArrowForwardIcon />}
            onClick={handleVerify}
          >
            {t("contentReview.verifyCta")}
          </Button>
        </>
      )}

      {checkable.length > 1 && (
        <>
          <Subtitle>{t("contentReview.foundMany", { count: checkable.length })}</Subtitle>
          <FormControl component="fieldset" sx={{ width: "100%" }}>
            <RadioGroup
              value={selected?.id ?? ""}
              onChange={(_, id) => setSelected(checkable.find((c) => c.id === id) ?? null)}
            >
              {checkable.map((claim) => (
                <FormControlLabel
                  key={claim.id}
                  value={claim.id}
                  control={<Radio color="primary" />}
                  label={claim.source_text}
                  sx={{ mb: 1, alignItems: "center" }}
                />
              ))}
            </RadioGroup>
          </FormControl>
          <Button
            variant="contained"
            size="large"
            endIcon={<ArrowForwardIcon />}
            disabled={!selected}
            onClick={handleVerify}
          >
            {t("contentReview.verifyCta")}
          </Button>
        </>
      )}
    </PageContainer>
  );
}
